import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import api from '@/utils/api';

// Minimal CSV parser supporting quoted fields and commas
function parseCSV(text) {
  const rows = [];
  let current = '';
  let row = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const next = text[i + 1];
    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      row.push(current);
      current = '';
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      if (current !== '' || row.length) {
        row.push(current);
        rows.push(row);
        row = [];
        current = '';
      }
      // Handle CRLF (\r\n)
      if (char === '\r' && next === '\n') i++;
    } else {
      current += char;
    }
  }
  if (current !== '' || row.length) {
    row.push(current);
    rows.push(row);
  }
  return rows.filter(r => r.length && r.some(c => String(c).trim() !== ''));
}

function normalizeBool(v) {
  if (typeof v === 'boolean') return v;
  const s = String(v || '').trim().toLowerCase();
  return s === 'true' || s === '1' || s === 'yes' || s === 'y';
}

export default function BulkImport({ onImported }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const headers = [
    'title','description','price','location','bedrooms','bathrooms','square_feet','category','status','is_featured','featured_image','floor_plan','video_tour','images'
  ];

  const downloadTemplate = () => {
    const sample = [
      headers.join(','),
      '"Sample Property","Beautiful home",250000000,"Kampala",3,2,1200,"house","available",false,"","","","https://example.com/image1.jpg;https://example.com/image2.jpg"'
    ].join('\n');
    const blob = new Blob([sample], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'properties-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      if (rows.length < 2) throw new Error('CSV must include header and at least one row');
      const headerRow = rows[0].map(h => h.trim());
      // Validate required headers exist
      const required = ['title','description','price','location','bedrooms','bathrooms','square_feet','category'];
      for (const h of required) {
        if (!headerRow.includes(h)) throw new Error(`Missing required column: ${h}`);
      }
      const indices = Object.fromEntries(headerRow.map((h, i) => [h, i]));
      const data = rows.slice(1).map(r => {
        const get = (name) => r[indices[name]] ?? '';
        const imagesCell = get('images');
        const images = imagesCell ? String(imagesCell).split(';').map(s => s.trim()).filter(Boolean) : [];
        return {
          title: String(get('title')).trim(),
          description: String(get('description')).trim(),
          price: parseFloat(get('price')),
          location: String(get('location')).trim(),
          bedrooms: parseInt(get('bedrooms'), 10),
          bathrooms: parseInt(get('bathrooms'), 10),
          square_feet: parseInt(get('square_feet'), 10),
          category: String(get('category')).trim() || 'house',
          status: String(get('status') || 'available').trim() || 'available',
          is_featured: normalizeBool(get('is_featured')),
          featured_image: String(get('featured_image') || '').trim() || null,
          floor_plan: String(get('floor_plan') || '').trim() || null,
          video_tour: String(get('video_tour') || '').trim() || null,
          images,
        };
      }).filter(item => item.title && item.description && !Number.isNaN(item.price));

      if (data.length === 0) throw new Error('No valid rows found');

      await api.post('/properties/bulk', data);
      onImported?.();
      alert('Bulk import successful');
    } catch (err) {
      console.error('Bulk import failed:', err);
      setError(err.message || 'Bulk import failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="flex items-center gap-3">
      <Button variant="outline" onClick={downloadTemplate}>Download CSV Template</Button>
      <Label className="cursor-pointer">
        <input type="file" accept=".csv,text/csv,application/vnd.ms-excel" className="hidden" onChange={handleUpload} />
        <span className="inline-block px-4 py-2 border rounded-md">{uploading ? 'Uploading...' : 'Upload CSV'}</span>
      </Label>
      {error && <span className="text-red-600 text-sm">{error}</span>}
    </div>
  );
}
